"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssessmentTemplate = void 0;
var isEmpty_1 = __importDefault(require("lodash/isEmpty"));
var uniq_1 = __importDefault(require("lodash/uniq"));
var uniqWith_1 = __importDefault(require("lodash/uniqWith"));
var flatten_1 = __importDefault(require("lodash/flatten"));
var constants_1 = require("../constants");
/**
 * @property {string}         id
 * @property {string}         title
 * @property {string}         subtitle
 * @property {string}         curriculum            - curriculum identifier
 * @property {string}         module                - curriculum's module identifier
 * @property {string}         grade                 - curriculum's grade identifier
 * @property {string}         topic                 - curriculum's topic identifier
 * @property {AssessmentType} type                  - curriculum's type identifier
 * @property {number}         sequence              - indicates the position in the module for this assessment
 * @property {string}         subject               - curriculum's subject
 * @property {string}         externalId            - assessment template external identifier. i.e Learnosity reference
 * @property {string}         totalItems
 * @property {string}         settings              - assessments settings, i.e Learnosity settings
 * @property {User}           owner                 - optional user information, available when it is a custom assessment template
 * @property {Item[]}         items                 - items details
 * @property {boolean}        manualScoring         - Autoscorable: true / open ended:false
 * @property {string}         dok                   - Depth of Knowledge
 * @property {Date}           createdAt             - assessment template createdAt date
 * @property {Stimulus}       stimulus              - stimulus object
 * @property {string}         language              - Assessment language
 */
var AssessmentTemplate = /** @class */ (function () {
    function AssessmentTemplate(data) {
        if (data === void 0) { data = { type: constants_1.AssessmentType.Assessment }; }
        Object.assign(this, data);
    }
    Object.defineProperty(AssessmentTemplate.prototype, "isEurekaMath", {
        get: function () {
            var _a;
            return (_a = this.curriculum) === null || _a === void 0 ? void 0 : _a.startsWith(constants_1.CurriculumId.EurekaMath);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isWitAndWisdom", {
        get: function () {
            var _a;
            return (_a = this.curriculum) === null || _a === void 0 ? void 0 : _a.startsWith(constants_1.CurriculumId.WitAndWisdom);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isAllManualScored", {
        get: function () {
            return this.items.every(function (_a) {
                var manualScoring = _a.manualScoring;
                return !!manualScoring;
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isAllAutoScored", {
        get: function () {
            return this.items.every(function (_a) {
                var manualScoring = _a.manualScoring;
                return !manualScoring;
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "hasExclusivelyRatingItems", {
        get: function () {
            return this.items.every(function (item) { return item.isRatingScoringType(); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "hasRubricItems", {
        get: function () {
            return this.items.some(function (item) { return item.hasRubricReferences(); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "hasMatrixItems", {
        get: function () {
            return this.items.some(function (item) { return item.isMatrixScoringType(); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "hasRatingItems", {
        get: function () {
            return this.items.some(function (item) { return item.isRatingScoringType(); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isNewRead", {
        get: function () {
            return ((this.items.some(function (item) { return item.isPassageScoringType(); }) && !this.isAllManualScored) ||
                this.items.every(function (item) { return item.isPassageScoringType(); }));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "hasBothManualAndAutoScored", {
        get: function () {
            return !this.isAllAutoScored && !this.isAllManualScored;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "standards", {
        /**
         * Returns all unique question standards
         * @returns {string[]}
         */
        get: function () {
            var _standards = [];
            (this.itemStandards || []).forEach(function (itemStandard) {
                var _a;
                _standards = _standards.concat((_a = itemStandard.standardGlobalIds) === null || _a === void 0 ? void 0 : _a.map(function (globalId) { return globalId; }));
            });
            _standards = _standards.filter(function (standard) { return !!standard; });
            return (0, uniq_1.default)(_standards);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "standardIds", {
        /**
         * Returns all unique question standard ids (v2)
         * @returns {string[]}
         */
        get: function () {
            var _standards = [];
            (this.itemStandards || []).forEach(function (itemStandard) {
                _standards = _standards.concat(itemStandard.standardId);
            });
            return (0, uniq_1.default)(_standards).filter(function (standardId) { return !!standardId; }); // dedupes and removes null/undefined values
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "v2ItemStandards", {
        /**
         * Returns all unique question standard ids (v2)
         * @returns {string[]}
         */
        get: function () {
            var _itemStandards = (this.itemStandards || []).filter(function (itemStandard) { return !!itemStandard.standardId; });
            return (0, uniqWith_1.default)(_itemStandards, function (a, b) { return a.standardId && a.standardId === b.standardId; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "itemExternalIds", {
        /**
         * Returns all question externalIds
         * @returns {string[]}
         */
        get: function () {
            return (this.items || []).map(function (item) { return item.externalId; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "totalItems", {
        get: function () {
            return (this.items || []).length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "coreItems", {
        get: function () {
            return (this.items || []).filter(function (_a) {
                var isBranch = _a.isBranch;
                return !isBranch;
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "totalCoreItems", {
        get: function () {
            return this.coreItems.length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isDraft", {
        get: function () {
            return this.status === constants_1.AssessmentTemplateStatus.Draft;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isPublished", {
        get: function () {
            return this.status === constants_1.AssessmentTemplateStatus.Published;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isDiagnostic", {
        get: function () {
            return this.type === constants_1.AssessmentType.Diagnostic;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isCustom", {
        get: function () {
            return !(0, isEmpty_1.default)(this.owner);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isTopicQuizAssignment", {
        get: function () {
            return this.assignmentType === constants_1.AssignmentType.TopicQuiz;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isMidModuleAssignment", {
        get: function () {
            return this.assignmentType === constants_1.AssignmentType.MidModuleAssessment;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isEndOfModuleAssignment", {
        get: function () {
            return this.assignmentType === constants_1.AssignmentType.EndOfModuleAssessment;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isVocabularyAssignment", {
        get: function () {
            return this.assignmentType === constants_1.AssignmentType.Vocabulary;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isModuleAssignment", {
        get: function () {
            return (this.assignmentType === constants_1.AssignmentType.MidModuleAssessment ||
                this.assignmentType === constants_1.AssignmentType.EndOfModuleAssessment ||
                this.assignmentType === constants_1.AssignmentType.ModuleAssessment);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isPureModuleAssignment", {
        get: function () {
            return this.assignmentType === constants_1.AssignmentType.ModuleAssessment;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AssessmentTemplate.prototype, "isBenchmarkAssignment", {
        get: function () {
            return this.assignmentType === constants_1.AssignmentType.Benchmark;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * It returns all the unique core items from the assessment template list
     * @param assessmentTemplates
     */
    AssessmentTemplate.findCoreItems = function (assessmentTemplates) {
        var allCoreItems = (assessmentTemplates || []).map(function (_a) {
            var coreItems = _a.coreItems;
            return coreItems;
        });
        return (0, uniqWith_1.default)((0, flatten_1.default)(allCoreItems || []), function (a, b) { return a.externalId === b.externalId; });
    };
    /**
     * It returns all the core items from the assessment template list
     * @param assessmentTemplates
     */
    AssessmentTemplate.findAllCoreItems = function (assessmentTemplates) {
        var allCoreItems = (assessmentTemplates || []).map(function (_a) {
            var coreItems = _a.coreItems;
            return coreItems;
        });
        return (0, flatten_1.default)(allCoreItems || []);
    };
    /**
     * It returns all items from the assessment template list
     * @param assessmentTemplates
     */
    AssessmentTemplate.findItems = function (assessmentTemplates) {
        var allItems = (assessmentTemplates || []).map(function (_a) {
            var items = _a.items;
            return items;
        });
        return (0, uniqWith_1.default)((0, flatten_1.default)(allItems), function (a, b) { return a.externalId === b.externalId; });
    };
    /**
     * It returns all items from the assessment template list
     * @param assessmentTemplates
     */
    AssessmentTemplate.findAllItems = function (assessmentTemplates) {
        var allItems = (assessmentTemplates || []).map(function (_a) {
            var items = _a.items;
            return items;
        });
        return (0, flatten_1.default)(allItems);
    };
    /**
     * It returns all questions from the items list
     * @param items
     */
    AssessmentTemplate.findQuestions = function (items) {
        var allQuestions = (items || []).map(function (_a) {
            var _b = _a.questions, questions = _b === void 0 ? [] : _b;
            return questions;
        });
        return (0, uniqWith_1.default)((0, flatten_1.default)(allQuestions), function (a, b) { return a.externalId === b.externalId; });
    };
    return AssessmentTemplate;
}());
exports.AssessmentTemplate = AssessmentTemplate;
